import { ApiEndpoints } from "@shared/api/api-endpoints";

import {
  WorkflowApiResponse,
  WorkflowRunApiResponse,
  CreateWorkflowRequest,
  UpdateWorkflowTriggerRequest,
  UpdateWorkflowActionsRequest,
  UpdateWorkflowResponse,
  CloneWorkflowResponse,
  Workflow,
  WorkflowsManagerProps,
  WorkflowFilters,
  MessageTemplate,
  WorkflowRunFilters,
  NotificationDetail,
} from "./types";

import { communicationApiClient } from "@shared/api/clients/apiClientFactory";
import { buildWorkflowFilters } from "./utils";

export const communicationWorkflowApi = {
  getMessageTemplates: async (
    remoteUtilityId: string,
    channelType: string
  ): Promise<MessageTemplate[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `get-templates`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("channel_type", channelType);
        query.push("is_active", 1);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data.result || [];
  },

  getWorkflows: async (
    remoteUtilityId: string,
    filters: WorkflowFilters = {
      page: 0,
      limit: 0,
    }
  ): Promise<WorkflowApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        if (filters.isDraft !== null && filters.isDraft !== undefined) {
          query.push("is_draft", filters.isDraft.toString());
        }

        query.push("page", (filters.page || 1).toString());
        query.push("limit", (filters.limit || 50).toString());

        if (filters.searchData) {
          query.push("search_data", filters.searchData);
        }

        if (filters.status && filters.status.length > 0) {
          filters.status.forEach((status) => {
            if (status === "draft") {
              query.push("is_draft", "true");
            } else if (status === "active") {
              query.push("is_active", "true");
              query.push("is_draft", "false");
            } else if (status === "inactive") {
              query.push("is_active", "false");
              query.push("is_draft", "false");
            }
          });
        }

        if (filters.triggerType) {
          const triggerTypeValue = filters.triggerType;
          query.push("trigger_type", triggerTypeValue);
        }

        if (filters.recipientType) {
          query.push("recipient_type", filters.recipientType);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data;
  },

  getWorkflowById: async (
    workflowId: number,
    utilityId: string
  ): Promise<Workflow> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    const workflow = response.data.results.find((w) => w.id === workflowId);

    if (!workflow) {
      throw new Error(`Workflow with ID ${workflowId} not found`);
    }
    return workflow;
  },

  createWorkflow: async (
    workflowData: CreateWorkflowRequest
  ): Promise<Workflow> => {
    const url = ApiEndpoints.createUrl("communication", `workflow`);
    const response = await communicationApiClient.post<{ result: Workflow }>(
      url,
      workflowData
    );
    return response.data.result;
  },
  patchCreatedWorkflow: async (
    workflowId: number,
    workflowData: CreateWorkflowRequest
  ): Promise<Workflow> => {
    const url = ApiEndpoints.createUrl(
      "communication",
      `workflow/${workflowId}`
    );
    const response = await communicationApiClient.patch<{ result: Workflow }>(
      url,
      workflowData
    );
    return response.data.result;
  },

  updateWorkflowTrigger: async (
    workflowId: number,
    triggerData: UpdateWorkflowTriggerRequest
  ): Promise<UpdateWorkflowResponse> => {
    const url = ApiEndpoints.createUrl(
      "communication",
      `workflow/${workflowId}`
    );
    const response = await communicationApiClient.patch(url, triggerData);
    return response.data;
  },

  updateWorkflowActions: async (
    workflowId: number,
    actionsData: UpdateWorkflowActionsRequest
  ): Promise<UpdateWorkflowResponse> => {
    const url = ApiEndpoints.createUrl(
      "communication",
      `workflow/${workflowId}`
    );
    const response = await communicationApiClient.patch(url, actionsData);
    return response.data;
  },

  activateWorkflow: async (
    workflowId: number,
    is_active: boolean
  ): Promise<UpdateWorkflowResponse> => {
    const url = ApiEndpoints.createUrl(
      "communication",
      `workflow/${workflowId}`
    );
    const response = await communicationApiClient.patch(url, {
      is_draft: false,
      is_active: is_active,
    });
    return response.data;
  },

  cloneWorkflow: async (workflowId: number): Promise<CloneWorkflowResponse> => {
    const url = ApiEndpoints.createUrl(
      "communication",
      `workflow-clone/${workflowId}`
    );
    const response = await communicationApiClient.post(url, undefined);
    return response.data;
  },

  getWorkflowRuns: async (
    remoteUtilityId: string,
    filters: WorkflowRunFilters = { page: 1, limit: 10 }
  ): Promise<WorkflowRunApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `notification-run`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);

        // Add pagination (with defaults)
        query.push("page", filters.page.toString());
        query.push("limit", filters.limit.toString());

        // Add search if provided
        if (filters.searchData) {
          query.push("search_data", filters.searchData);
        }

        // Add status filter if provided
        if (filters.statusDisplay) {
          query.push("status", filters.statusDisplay);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data;
  },

  getWorkflowRunById: async (
    runId: number,
    utilityId: string
  ): Promise<NotificationDetail> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `notifications`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("id", runId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data;
  },

  getEventType: async (): Promise<string[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `choices/event_types`
    );
    const response = await communicationApiClient.get(url);
    return response.data.result;
  },
};
